import { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import AvatarEditor from 'react-avatar-editor';
// @ts-ignore
import ColorThief from 'colorthief';
import ImageDropzone from '../ui/ImageDropzone';
import { adminService } from 'src/services/admin.service';
import { rgbToHex } from 'src/utils/rgbToHex';

interface ImageState {
  x: number;
  y: number;
  width?: number;
  height?: number;
  resource?: HTMLImageElement;
}

interface VenueLogoModalProps {
  isOpen: boolean
  onClose: () => void
  venuePublicId: string
  logoUrl?: string
  logoZoomSize?: number
  hasCustomLogo?: boolean
}

const IMAGE_SIZE = 500;
const MIN_ZOOM_SIZE = 1;
const MAX_ZOOM_SIZE = 200;

const INIT_BG_CLR = '#ffffff';
const INIT_BORDER_CLR = '#2c6c54';
const INIT_BORDER_SIZE = 10;

const VenueLogoModal = ({ isOpen, onClose, venuePublicId, logoUrl, logoZoomSize, hasCustomLogo }: VenueLogoModalProps) => {
  const editorRef = useRef<AvatarEditor>(null)
  const imageWrapperRef = useRef<HTMLDivElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [isLoadingLogo, setIsLoadingLogo] = useState(false)
  const [shape, setShape] = useState<'square' | 'circle' | 'transparent'>('square')
  const [backgroundColor, setBackgroundColor] = useState(INIT_BG_CLR)
  const [borderColor, setBorderColor] = useState(INIT_BORDER_CLR)
  const [dominantColor, setDominantColor] = useState<null | string>(null)
  const [zoom, setZoom] = useState(100)
  const [mapLogoZoom, setMapLogoZoom] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [borderSize, setBorderSize] = useState(0)
  const [imageFile, setImageFile] = useState<string | File | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setImageFile(null)
      setShape('transparent')
      setBackgroundColor(INIT_BG_CLR)
      setBorderColor(INIT_BORDER_CLR)
      setZoom(100)
      setRotation(0)
      return
    }

    const initialImage = logoUrl ?? null

    setIsUploading(false)
    setIsResetting(false)
    setMapLogoZoom(Math.ceil((logoZoomSize ?? 1.4) * 100))

    setImageFile(prev => {
      if (prev === initialImage) return prev
      setIsLoadingLogo(Boolean(initialImage))
      return initialImage
    })

  }, [isOpen, logoUrl, logoZoomSize])

  useEffect(() => {
    if (!imageFile) return
    extractDominantColor(imageFile)
  }, [imageFile])

  useEffect(() => {
    if (shape === 'transparent') {
      return
    }
    setBorderSize(prev => prev === 0 ? INIT_BORDER_SIZE : prev)
  }, [shape])

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const el = imageWrapperRef.current
      if (!el) return

      if (!el.contains(e.target as Node)) return

      e.preventDefault()

      if (e.altKey) {
        setRotation(prev => prev + e.deltaY * 0.1)
        return
      }

      const delta = -e.deltaY * 0.002
      setZoom(prev => Math.max(prev + Math.ceil(delta * 100), MIN_ZOOM_SIZE))
    }

    document.addEventListener("wheel", handleWheel, { passive: false })

    return () => {
      document.removeEventListener("wheel", handleWheel)
    }
  }, [])

  const imageLoadFailed = () => {
    setIsLoadingLogo(false)
    setImageFile(null)
  }

  const fileUploadHandle = async (file: File) => {
    setImageFile(file)
  }

  const extractDominantColor = async (
    src: string | File | Blob,
    applyColor = false
  ) => {
    try {
      const colorThief = new ColorThief()
      const img = new Image()

      img.crossOrigin = 'anonymous'

      let objectUrl: string | null = null

      if (typeof src === 'string') {
        img.src = src
      } else {
        objectUrl = URL.createObjectURL(src)
        img.src = objectUrl
      }

      await img.decode()

      const color = colorThief.getColor(img)
      const hex = rgbToHex(color[0], color[1], color[2])

      if (applyColor) {
        setBackgroundColor(hex)
      }

      setDominantColor(hex)

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    } catch (e) {
      console.error('Color extraction failed', e)
    }
  }

  const calculateContainScale = (
    imgWidth: number,
    imgHeight: number
  ) => {
    return Math.min(
      IMAGE_SIZE / imgWidth,
      IMAGE_SIZE / imgHeight
    )
  }

  const convertToShape = (canvas: HTMLCanvasElement) => {
    const finalCanvas = document.createElement('canvas')
    const ctx = finalCanvas.getContext('2d')
    if (!ctx) return canvas

    const size = Math.min(canvas.width, canvas.height)
    finalCanvas.width = size
    finalCanvas.height = size

    const center = size / 2

    if (shape === 'circle') {
      ctx.beginPath()
      ctx.arc(center, center, size / 2, 0, Math.PI * 2)
      ctx.closePath()
      ctx.clip()
    }

    if (shape !== 'transparent') {
      ctx.fillStyle = backgroundColor
      ctx.fillRect(0, 0, size, size)
    }

    ctx.drawImage(
      canvas,
      (canvas.width - size) / 2,
      (canvas.height - size) / 2,
      size,
      size,
      0,
      0,
      size,
      size
    )

    if (shape !== 'transparent' && borderSize > 0) {
      ctx.save()

      if (shape === 'circle') {
        ctx.beginPath()
        ctx.arc(
          center,
          center,
          size / 2 - borderSize / 2,
          0,
          Math.PI * 2
        )
        ctx.closePath()
        ctx.lineWidth = borderSize
        ctx.strokeStyle = borderColor
        ctx.stroke()
      } else {
        ctx.lineWidth = borderSize
        ctx.strokeStyle = borderColor
        ctx.strokeRect(
          borderSize / 2,
          borderSize / 2,
          size - borderSize,
          size - borderSize
        )
      }

      ctx.restore()
    }

    return finalCanvas
  }

  const submitHandle = () => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas()
      const finalCanvas = convertToShape(canvas);
      setIsUploading(true)
      finalCanvas.toBlob(async (blob) => {
        if (!blob) {
          setIsUploading(false)
          return
        }
        const file = new File(
          [blob],
          'logo.png',
          { type: blob.type || 'image/png' }
        )
        await adminService.updateVenueLogo(
          venuePublicId,
          file,
          { mapLogoZoom: Number((mapLogoZoom / 100).toFixed(2)), }
        )
        window.location.reload()
      })
    }
  }

  const resetLogoHandle = async () => {
    if (!hasCustomLogo) return
    if (!window.confirm('Reset this venue logo to the default image for its type?')) return

    setIsResetting(true)
    try {
      await adminService.resetVenueLogo(venuePublicId)
      window.location.reload()
    } catch (e) {
      console.error('Failed to reset venue logo', e)
      setIsResetting(false)
    }
  }

  const changeFileHandle = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/webp'

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        setImageFile(file)
      }
    }

    input.click()
  }

  const handleOnImageLoadSuccess = (img: ImageState) => {
    setIsLoadingLogo(false)
    if (!img.width || !img.height) return
    const scale = calculateContainScale(
      img.width,
      img.height
    )
    if (scale === 1) return
    setZoom(
      Math.max(Math.ceil(scale * 100) - 2, 1)
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      className="modal-popup"
      overlayClassName="modal-overlay"
      onRequestClose={onClose}
    >
      <div className="venue-logo-popup-content modal-content-wrapper">
        <h2>Edit Logo</h2>

        {(isLoadingLogo || isUploading || isResetting) && (
          <div className="loader-wrapper">
            <div className="loader"></div>
          </div>
        )}

        <div className="logo-content-wrapper modal-body-content">
          {!imageFile && (
            <ImageDropzone
              onFileAccepted={fileUploadHandle}
              maxMbSize={8}
            />
          )}

          {imageFile && (
            <>
              <div className="editor-wrapper">
                <div className="img-outter-wrapper">
                  <div
                    className="img-wrapper"
                    style={{
                      backgroundColor: shape === 'transparent' ? 'transparent' : backgroundColor,
                      borderRadius: shape === 'circle' ? '50%' : 0,
                      borderWidth: shape === 'transparent' ? 0 : borderSize,
                      borderStyle: 'solid',
                      borderColor: borderColor
                    }}
                    ref={imageWrapperRef}
                  >
                    <AvatarEditor
                      ref={editorRef}
                      image={imageFile}
                      width={IMAGE_SIZE}
                      height={IMAGE_SIZE}
                      scale={zoom / 100}
                      rotate={rotation}
                      disableBoundaryChecks
                      crossOrigin='anonymous'
                      style={{
                        top: shape === 'transparent' ? 0 : -borderSize,
                        left: shape === 'transparent' ? 0 : -borderSize,
                      }}
                      border={0}
                      color={[0, 0, 0, 0]}
                      onLoadFailure={imageLoadFailed}
                      onLoadSuccess={handleOnImageLoadSuccess}
                    />
                  </div>
                  <div className="grid-overlay" />
                </div>

                <div className="editor-tools">
                  <div className="form-item tool-group">
                    <label>Rotate</label>
                    <input
                      type="range"
                      min={0}
                      max={360}
                      step={1}
                      value={rotation}
                      onChange={(e) => setRotation(parseFloat(e.target.value))}
                    />
                  </div>

                  <div className="form-item tool-group">
                    <label>Zoom</label>
                    <div className='input-row'>
                      <input
                        type='number'
                        min={MIN_ZOOM_SIZE}
                        max={MAX_ZOOM_SIZE}
                        value={zoom}
                        onChange={(e) => setZoom(Math.min(MAX_ZOOM_SIZE, parseFloat(e.target.value)))}
                      />
                      <input
                        type="range"
                        min={MIN_ZOOM_SIZE}
                        max={MAX_ZOOM_SIZE}
                        step={1}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="form-item tool-group">
                    <label>Border</label>
                    <div className='input-row'>
                      <input
                        type='number'
                        min={0}
                        max={25}
                        value={borderSize}
                        onChange={(e) => setBorderSize(Math.min(50, parseInt(e.target.value)))
                        }
                      />
                      <input
                        type="range"
                        min={0}
                        max={25}
                        step={1}
                        value={borderSize}
                        onChange={(e) => setBorderSize(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="form-item tool-group">
                    <label>Map Scaling</label>
                    <div className='input-row'>
                      <input
                        type='number'
                        min={0}
                        max={300}
                        value={mapLogoZoom}
                        onChange={(e) => setMapLogoZoom(Math.min(300, parseInt(e.target.value)))}
                      />
                      <input
                        type="range"
                        min={50}
                        max={300}
                        step={1}
                        value={mapLogoZoom}
                        onChange={(e) => setMapLogoZoom(parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="buttons-group tool-group">
                    <button className="styled-button" onClick={() => setShape('transparent')}>Transparent</button>
                    <button className="styled-button" onClick={() => setShape('square')}>Square</button>
                    <button className="styled-button" onClick={() => setShape('circle')}>Circle</button>
                  </div>
                </div>
              </div>
              <div
                className='editor-tools editor-wrapper'
                style={{ justifyContent: 'flex-start' }}
              >
                <div className="form-item tool-group">
                  <label>Border color</label>
                  <div className="color-input-row">
                    <input
                      type="color"
                      value={borderColor}
                      disabled={shape === 'transparent'}
                      onChange={(e) => setBorderColor(e.target.value)}
                    />
                    <button
                      className="styled-button"
                      disabled={!dominantColor || shape === 'transparent'}
                      onClick={() => setBorderColor(dominantColor!)}
                    >
                      Apply Dominant Color
                    </button>
                  </div>
                </div>

                <div className="form-item tool-group">
                  <label>Background</label>
                  <div className="color-input-row">
                    <input
                      type="color"
                      value={backgroundColor}
                      disabled={shape === 'transparent'}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                    />
                    <button
                      className="styled-button"
                      disabled={!dominantColor || shape === 'transparent'}
                      onClick={() => setBackgroundColor(dominantColor!)}
                    >
                      Apply Dominant Color
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer-content">
          {hasCustomLogo && (
            <button
              className="styled-button"
              style={{ marginRight: 'auto' }}
              onClick={resetLogoHandle}
              disabled={isUploading || isResetting || isLoadingLogo}
            >
              {isResetting ? 'Resetting…' : 'Reset to default'}
            </button>
          )}
          <button
            className="styled-button"
            onClick={changeFileHandle}
            disabled={isUploading || isResetting || !imageFile || isLoadingLogo}
          >
            Change File
          </button>
          <button
            className="styled-button"
            onClick={submitHandle}
            disabled={isUploading || isResetting || !imageFile || isLoadingLogo}
          >
            {isUploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VenueLogoModal;